import {ProductJsonLd} from 'next-seo';
import {getProductUrl, Product} from '@/_application/modules/products/model/product';
import {getProductBrand} from '@/_application/modules/products/model/brand';
import {getFinalPrice, getMaximumPrice, getMinimalPrice} from '@/_application/modules/products/model/price';
import {Review} from '@/__generated__/__types__';

type ReviewSchema = {
    author: {name: string};
    reviewRating: {ratingValue: number};
};
export const CategoryProductsSeo = ({product, reviews = []}: {product: Product; reviews?: Review[]}) => {
    const finalPrice = getFinalPrice(product);
    const minimalPrice = getMinimalPrice(product);
    const maxPrice = getMaximumPrice(product);

    let now = new Date();
    // Convert to UTC+7
    now.setHours(now.getHours() + 7);
    let endOfDay = new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999);

    const reviewSchemas: ReviewSchema[] = reviews?.map(item => {
        return {
            author: {name: item?.nickname ?? ''},
            reviewRating: {ratingValue: +(item?.value ?? 0)},
        };
    });

    return (
        <ProductJsonLd
            keyOverride={product.sku}
            productName={product.name ?? 'KidsPlaza Product'}
            images={[
                product.small_image?.url ?? 'https://cdn-v2.kidsplaza.vn/media/gallery/image/kidsplaza_share_image.jpg',
            ]}
            description={product.meta_description ?? 'KidsPlaza Product Description'}
            brand={getProductBrand(product)?.label ?? 'KidsPlaza Brand'}
            aggregateRating={{
                ratingValue: product.ratings_summary ? Math.ceil(product.ratings_summary / 20) : 5,
                reviewCount: product.ratings_count || 1,
            }}
            reviews={reviewSchemas}
            offers={
                product.__typename === 'ConfigurableProduct'
                    ? [
                          {
                              hasMerchantReturnPolicy: {
                                  '@type': 'MerchantReturnPolicy',
                                  applicableCountry: 'VN',
                                  returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
                                  merchantReturnDays: 45,
                                  returnMethod: 'https://schema.org/ReturnByMail',
                                  returnFees: 'https://schema.org/FreeReturn',
                              },
                              price: minimalPrice.value,
                              priceCurrency: minimalPrice.currency,
                              priceValidUntil: endOfDay,
                              itemCondition: 'https://schema.org/NewCondition',
                              availability: 'https://schema.org/InStock',
                              url: getProductUrl(product),
                              seller: {
                                  name: 'KidsPlaza',
                              },
                              shippingDetails: {
                                  '@type': 'OfferShippingDetails',
                                  shippingRate: {
                                      '@type': 'MonetaryAmount',
                                      value: 19000,
                                      currency: 'VND',
                                  },
                                  shippingDestination: {
                                      '@type': 'DefinedRegion',
                                      addressCountry: 'VN',
                                  },
                                  deliveryTime: {
                                      '@type': 'ShippingDeliveryTime',
                                      handlingTime: {
                                          '@type': 'QuantitativeValue',
                                          minValue: 0,
                                          maxValue: 1,
                                          unitCode: 'DAY',
                                      },
                                      transitTime: {
                                          '@type': 'QuantitativeValue',
                                          minValue: 1,
                                          maxValue: 5,
                                          unitCode: 'DAY',
                                      },
                                  },
                              },
                          },
                          {
                              hasMerchantReturnPolicy: {
                                  '@type': 'MerchantReturnPolicy',
                                  applicableCountry: 'VN',
                                  returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
                                  merchantReturnDays: 45,
                                  returnMethod: 'https://schema.org/ReturnByMail',
                                  returnFees: 'https://schema.org/FreeReturn',
                              },
                              price: maxPrice.value,
                              priceCurrency: maxPrice.currency,
                              priceValidUntil: endOfDay,
                              itemCondition: 'https://schema.org/NewCondition',
                              availability: 'https://schema.org/InStock',
                              url: getProductUrl(product),
                              seller: {
                                  name: 'KidsPlaza',
                              },
                              shippingDetails: {
                                  '@type': 'OfferShippingDetails',
                                  shippingRate: {
                                      '@type': 'MonetaryAmount',
                                      value: 19000,
                                      currency: 'VND',
                                  },
                                  shippingDestination: {
                                      '@type': 'DefinedRegion',
                                      addressCountry: 'VN',
                                  },
                                  deliveryTime: {
                                      '@type': 'ShippingDeliveryTime',
                                      handlingTime: {
                                          '@type': 'QuantitativeValue',
                                          minValue: 0,
                                          maxValue: 1,
                                          unitCode: 'DAY',
                                      },
                                      transitTime: {
                                          '@type': 'QuantitativeValue',
                                          minValue: 1,
                                          maxValue: 5,
                                          unitCode: 'DAY',
                                      },
                                  },
                              },
                          },
                      ]
                    : [
                          {
                              hasMerchantReturnPolicy: {
                                  '@type': 'MerchantReturnPolicy',
                                  applicableCountry: 'VN',
                                  returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
                                  merchantReturnDays: 45,
                                  returnMethod: 'https://schema.org/ReturnByMail',
                                  returnFees: 'https://schema.org/FreeReturn',
                              },
                              price: finalPrice?.value,
                              priceCurrency: finalPrice?.currency,
                              priceValidUntil: endOfDay,
                              itemCondition: 'https://schema.org/NewCondition',
                              availability: 'https://schema.org/InStock',
                              url: getProductUrl(product),
                              seller: {
                                  name: 'KidsPlaza',
                              },
                              shippingDetails: {
                                  '@type': 'OfferShippingDetails',
                                  shippingRate: {
                                      '@type': 'MonetaryAmount',
                                      value: 19000,
                                      currency: 'VND',
                                  },
                                  shippingDestination: {
                                      '@type': 'DefinedRegion',
                                      addressCountry: 'VN',
                                  },
                                  deliveryTime: {
                                      '@type': 'ShippingDeliveryTime',
                                      handlingTime: {
                                          '@type': 'QuantitativeValue',
                                          minValue: 0,
                                          maxValue: 1,
                                          unitCode: 'DAY',
                                      },
                                      transitTime: {
                                          '@type': 'QuantitativeValue',
                                          minValue: 1,
                                          maxValue: 5,
                                          unitCode: 'DAY',
                                      },
                                  },
                              },
                          },
                      ]
            }
            mpn={`${product.id ?? 'KidsPlaza Product ID'}`}
            sku={`${product.sku ?? 'KidsPlaza Product SKU'}`}
        />
    );
};
